import { AppRouteRecordRaw } from '../types';
import { Roles } from '@/constants/roles';

const Body = () => import('@/components/body.vue');
const FindDataByInvoiceNumber = () =>
  import('@/pages/ryd-customer-support/invoice/FindDataByInvoiceNumber.vue');
const FindDataByOrderUuid = () =>
  import('@/pages/ryd-customer-support/order/FindDataByOrderUuid.vue');
const CustomerSupportUserManagement = () =>
  import('@/pages/ryd-customer-support/user-management/CustomerSupportUserManagement.vue');
const FindDataByEmailPartial = () =>
  import('@/pages/ryd-customer-support/user-email-search/FindDataByEmailPartial.vue');
const FindDataByPaymentEmail = () =>
  import('@/pages/ryd-customer-support/payment-email-search/FindDataByPaymentEmail.vue');
const FindDataByName = () => import('@/pages/ryd-customer-support/name-search/FindDataByName.vue');
const FindDataByVoucherCode = () =>
  import('@/pages/ryd-customer-support/voucher/FindDataByVoucherCode.vue');
const CustomerSupportOverview = () =>
  import('@/pages/ryd-customer-support/overview/CustomerSupportOverview.vue');
const RemoteFuelingList = () =>
  import('@/pages/ryd-customer-support/hacker-analytics/remote-fueling/RemoteFuelingList.vue');
const ViewRemoteFuelingRecordDetails = () =>
  import('@/pages/ryd-customer-support/hacker-analytics/remote-fueling/ViewRemoteFuelingRecordDetails.vue');
const SuspiciousDevicesList = () =>
  import('@/pages/ryd-customer-support/hacker-analytics/suspicious-devices/SuspiciousDevicesList.vue');
const ViewSuspiciousDeviceDetails = () =>
  import('@/pages/ryd-customer-support/hacker-analytics/suspicious-devices/ViewSuspiciousDeviceDetails.vue');
const GdprExportPage = () => import('@/pages/ryd-customer-support/gdpr-export/GdprExportPage.vue');
const DeeplinkResolverPage = () =>
  import('@/pages/ryd-customer-support/deeplink-resolver/DeeplinkResolverPage.vue');
// Support chat and AI Training moved to Internal Backoffice → AI Assistants (ai-assistants.routes.ts).
const CustomerStatusList = () =>
  import('@/pages/ryd-customer-support/customer-status/CustomerStatusList.vue');
// Request Inbox (info@ryd.one analytics, T-03 phase 1 dry run): one folder, one route record.

export const rydCustomerSupportRoutes: AppRouteRecordRaw[] = [
  {
    path: '/ryd-customer-support',
    component: Body,
    meta: {
      requiresAuth: true,
      requiredUserGroup: ['ryd'],
      breadcrumb: 'RYD Customer Support',
    },
    children: [
      {
        path: 'overview',
        name: 'customer-support-overview',
        component: CustomerSupportOverview,
        meta: {
          title: 'Customer Support Overview | ryd Portal',
          breadcrumb: 'Overview',
        },
      },
      {
        path: 'find-data-by-invoice-number',
        name: 'find-data-by-invoice-number',
        component: FindDataByInvoiceNumber,
        meta: {
          title: 'Find Data by Invoice Number | ryd Portal',
          breadcrumb: 'Find Data by Invoice Number',
        },
      },
      {
        path: 'find-data-by-order-uuid',
        name: 'find-data-by-order-uuid',
        component: FindDataByOrderUuid,
        meta: {
          title: 'Find Data by Order UUID | ryd Portal',
          breadcrumb: 'Find Data by Order UUID',
        },
      },
      {
        path: 'user-management',
        name: 'customer-support-user-management',
        component: CustomerSupportUserManagement,
        meta: {
          title: 'User Management | ryd Portal',
          breadcrumb: 'User Management',
        },
      },
      {
        path: 'find-data-by-email-partial',
        name: 'find-data-by-email-partial',
        component: FindDataByEmailPartial,
        meta: {
          title: 'Find Data by Email | ryd Portal',
          breadcrumb: 'Find Data by Email',
        },
      },
      {
        path: 'find-data-by-payment-email',
        name: 'find-data-by-payment-email',
        component: FindDataByPaymentEmail,
        meta: {
          title: 'Find Data by Payment Email | ryd Portal',
          breadcrumb: 'Find Data by Payment Email',
        },
      },
      {
        path: 'find-data-by-name',
        name: 'find-data-by-name',
        component: FindDataByName,
        meta: {
          title: 'Find Data by Name | ryd Portal',
          breadcrumb: 'Find Data by Name',
        },
      },
      {
        path: 'find-data-by-voucher-code',
        name: 'find-data-by-voucher-code',
        component: FindDataByVoucherCode,
        meta: {
          title: 'Find Data by Voucher Code | ryd Portal',
          breadcrumb: 'Find Data by Voucher Code',
        },
      },
      {
        path: 'deeplink-resolver',
        name: 'customer-support-deeplink-resolver',
        component: DeeplinkResolverPage,
        meta: {
          title: 'Deeplink Resolver | ryd Portal',
          breadcrumb: 'Deeplink Resolver',
        },
      },
      {
        path: 'customer-status-at-partner',
        name: 'customer-support-customer-status-at-partner',
        component: CustomerStatusList,
        meta: {
          title: 'Customer Status at Partner | ryd Portal',
          breadcrumb: 'Customer Status at Partner',
        },
      },
      {
        path: 'remote-fueling/records/:orderId',
        name: 'customer-support-remote-fueling-record',
        component: ViewRemoteFuelingRecordDetails,
        props: true,
        meta: {
          title: 'Remote Fueling Record | ryd Portal',
          breadcrumb: 'Remote Fueling Record',
        },
      },
      {
        path: 'remote-fueling',
        name: 'customer-support-remote-fueling',
        component: RemoteFuelingList,
        meta: {
          title: 'Remote Fueling Detection | ryd Portal',
          breadcrumb: 'Remote Fueling',
        },
      },
      {
        path: 'remote-fueling/:orderId',
        redirect: (to) => {
          const raw = to.params.orderId;
          const id = Array.isArray(raw) ? raw[0] : raw;
          if (!id || id === 'records') {
            return { name: 'customer-support-remote-fueling' };
          }
          return {
            name: 'customer-support-remote-fueling-record',
            params: { orderId: id },
          };
        },
      },
      {
        path: 'suspicious-devices/records/:deviceKey',
        name: 'customer-support-suspicious-device-record',
        component: ViewSuspiciousDeviceDetails,
        props: true,
        meta: {
          title: 'Suspicious Device Record | ryd Portal',
          breadcrumb: 'Suspicious Device Record',
        },
      },
      {
        path: 'suspicious-devices',
        name: 'customer-support-suspicious-devices',
        component: SuspiciousDevicesList,
        meta: {
          title: 'Suspicious Devices | ryd Portal',
          breadcrumb: 'Suspicious Devices',
        },
      },
      {
        path: 'gdpr-export',
        name: 'customer-support-gdpr-export',
        component: GdprExportPage,
        meta: {
          title: 'GDPR Export | ryd Portal',
          breadcrumb: 'GDPR Export',
          requiredRoles: [Roles.gdpr_admin],
        },
      },
      {
        path: 'account-deletions',
        name: 'customer-support-account-deletions',
        component: () =>
          import('@/pages/ryd-customer-support/account-deletions/search/AccountDeletionsPage.vue'),
        meta: {
          title: 'Account Deletions | ryd Portal',
          breadcrumb: 'Account Deletions',
          requiredRoles: [Roles.gdpr_admin],
        },
      },
      {
        // Opening a record runs an audited search by account id (D-13).
        path: 'account-deletions/:accountId',
        name: 'customer-support-account-deletion-record',
        component: () =>
          import('@/pages/ryd-customer-support/account-deletions/record/AccountDeletionRecordPage.vue'),
        meta: {
          title: 'Deletion Record | ryd Portal',
          headTitle: 'Deletion Record',
          breadcrumb: 'Deletion Record',
          requiredRoles: [Roles.gdpr_admin],
        },
      },
    ],
  },
];
